/**
 * Vector Math Utilities
 * 
 * Pure mathematical functions for 3D vector operations.
 * Used by ray casting, spatial navigation, and camera transformations.
 */

export type Vector3 = [number, number, number];
export type Vector4 = [number, number, number, number];
export type Matrix3x3 = [
  number, number, number,
  number, number, number,
  number, number, number
];
export type Quaternion = [number, number, number, number]; // [w, x, y, z] - COLMAP format

// ============================================================================
// Basic Vector Operations
// ============================================================================

/**
 * Add two vectors: a + b
 */
export function add(a: Vector3, b: Vector3): Vector3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

/**
 * Subtract two vectors: a - b
 */
export function subtract(a: Vector3, b: Vector3): Vector3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

/**
 * Multiply vector by scalar: v * s
 */
export function scale(v: Vector3, s: number): Vector3 {
  return [v[0] * s, v[1] * s, v[2] * s];
}

/**
 * Dot product: a · b
 * Returns scalar projection of a onto b
 */
export function dot(a: Vector3, b: Vector3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

/**
 * Cross product: a × b
 * Returns vector perpendicular to both a and b
 */
export function cross(a: Vector3, b: Vector3): Vector3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0]
  ];
}

/**
 * Vector length (magnitude): |v|
 */
export function length(v: Vector3): number {
  return Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
}

/**
 * Squared length (avoids sqrt, faster for comparisons)
 */
export function lengthSquared(v: Vector3): number {
  return v[0] * v[0] + v[1] * v[1] + v[2] * v[2];
}

/**
 * Distance between two points: |a - b|
 */
export function distance(a: Vector3, b: Vector3): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  const dz = a[2] - b[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Squared distance (avoids sqrt, faster for comparisons)
 */
export function distanceSquared(a: Vector3, b: Vector3): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  const dz = a[2] - b[2];
  return dx * dx + dy * dy + dz * dz;
}

/**
 * Normalize vector to unit length: v / |v|
 * Returns zero vector if input is zero vector
 */
export function normalize(v: Vector3): Vector3 {
  const len = length(v);
  if (len < 1e-10) {
    console.warn('⚠️ [vectorMath] Normalizing zero vector, returning [0,0,0]');
    return [0, 0, 0];
  }
  return [v[0] / len, v[1] / len, v[2] / len];
}

/**
 * Linear interpolation: lerp(a, b, t) = a + t * (b - a)
 * t=0 returns a, t=1 returns b
 */
export function lerp(a: Vector3, b: Vector3, t: number): Vector3 {
  return [
    a[0] + t * (b[0] - a[0]),
    a[1] + t * (b[1] - a[1]),
    a[2] + t * (b[2] - a[2])
  ];
}

// ============================================================================
// Quaternion Operations
// ============================================================================

/**
 * Convert quaternion to 3x3 rotation matrix
 * Quaternion format: [w, x, y, z] - COLMAP/COLMAP standard
 */
export function quaternionToMatrix(q: Quaternion): Matrix3x3 {
  const [w, x, y, z] = q;
  
  const x2 = x * x;
  const y2 = y * y;
  const z2 = z * z;
  const xy = x * y;
  const xz = x * z;
  const yz = y * z;
  const wx = w * x;
  const wy = w * y;
  const wz = w * z;

  return [
    1 - 2 * (y2 + z2), 2 * (xy - wz),     2 * (xz + wy),
    2 * (xy + wz),     1 - 2 * (x2 + z2), 2 * (yz - wx),
    2 * (xz - wy),     2 * (yz + wx),     1 - 2 * (x2 + y2)
  ];
}

/**
 * Multiply vector by 3x3 matrix: M * v
 */
export function multiplyMatrixVector(m: Matrix3x3, v: Vector3): Vector3 {
  return [
    m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
    m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
    m[6] * v[0] + m[7] * v[1] + m[8] * v[2]
  ];
}

/**
 * Quaternion multiplication: q1 * q2
 * Used for composing rotations
 */
export function multiplyQuaternions(q1: Quaternion, q2: Quaternion): Quaternion {
  const [w1, x1, y1, z1] = q1;
  const [w2, x2, y2, z2] = q2;
  
  return [
    w1 * w2 - x1 * x2 - y1 * y2 - z1 * z2,
    w1 * x2 + x1 * w2 + y1 * z2 - z1 * y2,
    w1 * y2 - x1 * z2 + y1 * w2 + z1 * x2,
    w1 * z2 + x1 * y2 - y1 * x2 + z1 * w2
  ];
}

/**
 * Quaternion conjugate (inverse for unit quaternions)
 */
export function conjugateQuaternion(q: Quaternion): Quaternion {
  return [q[0], -q[1], -q[2], -q[3]];
}

/**
 * Rotate vector by quaternion: q * v * q^-1
 */
export function rotateVectorByQuaternion(v: Vector3, q: Quaternion): Vector3 {
  // Convert to quaternion: [0, v.x, v.y, v.z]
  const vq: Quaternion = [0, v[0], v[1], v[2]];
  
  // q * v * q^-1
  const qConj = conjugateQuaternion(q);
  const temp = multiplyQuaternions(q, vq);
  const result = multiplyQuaternions(temp, qConj);
  
  return [result[1], result[2], result[3]];
}

/**
 * Calculate rotation difference between two quaternions (in degrees)
 * Returns angle in range [0, 180]
 */
export function rotationDifference(q1: Quaternion, q2: Quaternion): number {
  // Dot product of quaternions
  const dotProduct = Math.abs(
    q1[0] * q2[0] + q1[1] * q2[1] + q1[2] * q2[2] + q1[3] * q2[3]
  );
  
  // Clamp to [-1, 1] to avoid NaN from acos
  const clampedDot = Math.max(-1, Math.min(1, dotProduct));
  
  // Angle = 2 * acos(|q1 · q2|)
  const angleRadians = 2 * Math.acos(clampedDot);
  return (angleRadians * 180) / Math.PI;
}

// ============================================================================
// Projection & Camera Operations
// ============================================================================

/**
 * Project 3D world point to 2D camera coordinates
 * 
 * @param worldPoint - 3D point in world space
 * @param cameraPosition - Camera position in world space
 * @param cameraRotation - Camera rotation quaternion
 * @param focalLength - Camera focal length (pixels)
 * @param principalPoint - Principal point [cx, cy] (pixels)
 * @returns [x, y] pixel coordinates, or null if behind camera
 */
export function projectPoint(
  worldPoint: Vector3,
  cameraPosition: Vector3,
  cameraRotation: Quaternion,
  focalLength: number | [number, number], // fx or [fx, fy]
  principalPoint: [number, number]
): [number, number] | null {
  // Transform to camera space
  // COLMAP convention: 
  // - rotation quaternion (qw,qx,qy,qz) = world-to-camera rotation
  // - translation vector (tx,ty,tz) = negated camera center rotated into camera coords
  // - Camera center in world: C = -R^T * t = -R_inverse * t
  // - Point in camera space: P_cam = R * (P_world - C) = R * P_world + t
  
  // Method 1: Apply rotation to world point, then add translation
  const rotated = rotateVectorByQuaternion(worldPoint, cameraRotation);
  const cameraSpace: Vector3 = add(rotated, cameraPosition);
  
  // COLMAP/OpenCV convention: camera looks down +Z axis (positive Z is in front)
  const depth = cameraSpace[2];
  if (depth <= 0) {
    return null;
  }
  
  // Project to normalized image coordinates
  // OpenCV convention: X right, Y down, Z forward
  // TEST: Try without negation to match COLMAP viewer
  const normalizedX = cameraSpace[0] / depth;
  const normalizedY = cameraSpace[1] / depth;
  
  // Project to image plane
  // Handle both single focal length and separate fx/fy
  const [fx, fy] = typeof focalLength === 'number' 
    ? [focalLength, focalLength] 
    : focalLength;
  
  const x = (fx * normalizedX) + principalPoint[0];
  const y = (fy * normalizedY) + principalPoint[1];
  
  // Debug: Log if projection results in specific coordinates we see in samples
  if (Math.abs(x - 388) < 1 && Math.abs(y - 34) < 5) {
    console.log(`🔍 Projection to (388, 34):`);
    console.log(`   worldPoint: [${worldPoint[0].toFixed(2)}, ${worldPoint[1].toFixed(2)}, ${worldPoint[2].toFixed(2)}]`);
    console.log(`   cameraSpace: [${cameraSpace[0].toFixed(2)}, ${cameraSpace[1].toFixed(2)}, ${cameraSpace[2].toFixed(2)}]`);
    console.log(`   normalized: [${normalizedX.toFixed(4)}, ${normalizedY.toFixed(4)}]`);
    console.log(`   fx=${fx.toFixed(1)}, fy=${fy.toFixed(1)}, cx=${principalPoint[0]}, cy=${principalPoint[1]}`);
    console.log(`   calc: (${fx} * ${normalizedX.toFixed(4)} + ${principalPoint[0]}) = ${x.toFixed(1)}`);
    console.log(`   calc: (${fy} * ${normalizedY.toFixed(4)} + ${principalPoint[1]}) = ${y.toFixed(1)}`);
  }
  
  return [x, y];
}

/**
 * Calculate angle between two vectors (in degrees)
 * Returns angle in range [0, 180]
 */
export function angleBetween(a: Vector3, b: Vector3): number {
  const dotProd = dot(a, b);
  const lenA = length(a);
  const lenB = length(b);
  
  if (lenA < 1e-10 || lenB < 1e-10) {
    return 0;
  }
  
  const cosAngle = Math.max(-1, Math.min(1, dotProd / (lenA * lenB)));
  const angleRadians = Math.acos(cosAngle);
  return (angleRadians * 180) / Math.PI;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if vector is approximately zero
 */
export function isZeroVector(v: Vector3, epsilon = 1e-10): boolean {
  return lengthSquared(v) < epsilon * epsilon;
}

/**
 * Check if two vectors are approximately equal
 */
export function areVectorsEqual(a: Vector3, b: Vector3, epsilon = 1e-6): boolean {
  return (
    Math.abs(a[0] - b[0]) < epsilon &&
    Math.abs(a[1] - b[1]) < epsilon &&
    Math.abs(a[2] - b[2]) < epsilon
  );
}

/**
 * Clamp value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
